import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiLock, FiEdit2, FiTrash2 } from "react-icons/fi";
import { useControlDate, useActivePhases } from "hooks/useControlDate";
import { DateRangeDisplay } from "components/ui/DateRangeDisplay";
import { api } from "services/api";
import { getResultadoLabel } from "utils/iniciativa-categoria";

interface CardIniciativaELBBProps {
  entrega: any;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate?: () => void;
  currentUserId?: string;
}

export function CardIniciativaELBB({
  entrega,
  index,
  isExpanded,
  onToggle,
  onUpdate,
  currentUserId,
}: CardIniciativaELBBProps) {
  const phases = useActivePhases();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const dentroPeridoCadastro = phases.canAccessCadastroELBB && !phases.elbbConfirmation;
  const periodoConfirmacaoElbb = useControlDate('confirmacao_inscricao_elbb');
  const periodoInscricaoElbb = useControlDate('etapa_inscricao_elbb');

  const handleEdit = () => {
    navigate("/cadastroELBB", { state: { editData: entrega } });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setDeleteSuccess(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      await api.put(`/database/bb_task__c`, {
        ...entrega,
        active: false,
      });
      setDeleteSuccess(true);
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeleteSuccess(false);
        if (onUpdate) {
          onUpdate();
        }
      }, 2000);
    } catch (error) {
      console.error("Erro ao inativar iniciativa:", error);
      alert("Erro ao inativar a iniciativa. Tente novamente.");
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteSuccess(false);
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 lg:p-6 bg-white">
      <div className="space-y-2.5 sm:space-y-3">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
          <div className="flex-1 w-full sm:w-auto">
            <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">
              Nome da Iniciativa:
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm break-words">{entrega.title}</p>
          </div>
          {dentroPeridoCadastro && currentUserId === entrega.owner && (
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleEdit}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 text-xs sm:text-sm text-blue-600 hover:text-blue-700 active:text-blue-800 hover:bg-blue-50 rounded transition-colors touch-manipulation"
                title="Editar iniciativa"
              >
                <FiEdit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Editar
              </button>
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 text-xs sm:text-sm text-red-600 hover:text-red-700 active:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 touch-manipulation"
                title="Deletar iniciativa"
              >
                <FiTrash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {isDeleting ? "Deletando..." : "Deletar"}
              </button>
            </div>
          )}
        </div>

        {/* <div>
          <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">
            Perspectiva ECBB:
          </h3>
          <p className="text-gray-600 text-xs sm:text-sm">{entrega.indicator?.nome}</p>
        </div> */}

        <div>
          <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">
            Perspectiva ECBB:
          </h3>
          <p className="text-gray-600 text-xs sm:text-sm">{entrega.indicator?.perspectiva}</p>
        </div>

        <div>
          <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">
            Descrição:
          </h3>
          <p className="text-gray-600 text-xs sm:text-sm break-words">{entrega.description}</p>
        </div>

        {isExpanded && (
          <div className="pt-3 sm:pt-4 space-y-3 sm:space-y-4">
            {(() => {
              const labelResultado = getResultadoLabel(entrega.categoryPremio, entrega.indicator, entrega.result);
              if (!labelResultado.mostrar) return null;
              return (
                <div className="bg-blue-50 border-l-4 border-blue-600 p-3 sm:p-4">
                  <p className="text-blue-800 text-xs sm:text-sm">
                    <span className="font-semibold">{labelResultado.titulo}</span>
                  </p>
                  <p className="text-gray-700 text-xs sm:text-sm mt-2">
                    {entrega.description}
                  </p>
                </div>
              );
            })()}

            {/* Seção do Responsável da Realização */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 sm:mb-3 text-sm sm:text-base">
                Responsável:
              </h3>
              <div className="flex items-center gap-2 sm:gap-3">
                {entrega.owner_avatar?.image?.small?.url ? (
                  <img
                    src={entrega.owner_avatar.image.small.url}
                    alt={entrega.owner_name}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0">
                    {entrega.owner_name?.[0] || "?"}
                  </div>
                )}
                <div>
                  <p className="text-gray-800 font-medium text-xs sm:text-sm">
                    {entrega.owner_name || "Não informado"}
                  </p>
                  {entrega.owner_funcao && (
                    <p className="text-gray-600 text-xs">{entrega.owner_funcao}</p>
                  )}
                  {entrega.owner_nome_dependencia && (
                    <p className="text-gray-500 text-xs">{entrega.owner_nome_dependencia}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Seção do Gestor */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 sm:mb-3 text-sm sm:text-base">
                Gestor Responsável:
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0">
                    {entrega.original_manager_detail?.nome_guerra?.[0] ||
                      entrega.original_manager_detail?.nome?.[0] ||
                      "G"}
                  </div>
                  <span className="text-gray-700 text-xs sm:text-sm break-words">
                    {entrega.original_manager_detail?.nome_guerra ||
                      entrega.original_manager_detail?.nome ||
                      "Gestor não informado"}
                    {entrega.original_manager_detail?.chave &&
                      ` (${entrega.original_manager_detail.chave})`}
                  </span>
                </div>
              </div>
            </div>

            {/* Seção de Participantes */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-2 sm:mb-3 text-sm sm:text-base">
                Participantes:
              </h3>
              <div className="space-y-2">
                {entrega.team?.map((member: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0">
                      {member.name?.[0] || "?"}
                    </div>
                    <span className="text-gray-700 text-xs sm:text-sm break-words">
                      {member.name || member._id} ({member.code || member._id})
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* AGENDA DE APROVAÇÃO — desativada temporariamente.
                Para reativar: descomente o bloco abaixo e certifique-se de que
                periodoCadastroElbb (useControlDate('periodo_cadastro_elbb')) e
                DateRangeDisplay continuam importados.
                Status mapeados: "aprovada" → barra verde, "reprovada" → barra vermelha,
                pendente → exibe o período de cadastro via DateRangeDisplay. */}
            <div className="bg-purple-100 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-purple-900 mb-3 text-sm sm:text-base">
                Agenda de aprovação
              </h3>
              <div className="bg-white rounded-lg p-3 sm:p-4">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiLock className="w-4 h-4 text-gray-500" />
                      <p className="text-xs sm:text-sm text-gray-700">
                        Gestor Responsável: {entrega.original_manager_detail?.nome_guerra || entrega.original_manager_detail?.nome || "Nome do Gestor"}
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          entrega.status === "aprovada"
                            ? "bg-green-bb-2"
                            : entrega.status === "reprovada"
                            ? "bg-red-500"
                            : "bg-gray-300"
                        }`}
                        style={{ width: entrega.status === "aprovada" || entrega.status === "reprovada" ? "100%" : "0%" }}
                      ></div>
                    </div>
                    {entrega.status === "aprovada" ? (
                      <div className="mt-1 space-y-3">
                        <p className="text-xs text-green-bb-2 font-semibold">
                          Aprovada
                        </p>

                        {/* Próxima etapa: inscrição */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-green-800 mb-1">
                            Próxima etapa, complete sua inscrição
                            {periodoInscricaoElbb && (
                              <span className="font-normal text-green-700">
                                {" "}— <DateRangeDisplay
                                  dataInicio={periodoInscricaoElbb.dataInicio}
                                  dataFim={periodoInscricaoElbb.dataFim}
                                  dateFormat="dd/MM"
                                  uppercase={false}
                                />
                              </span>
                            )}
                          </p>
                          <ul className="text-xs text-green-800 space-y-1 list-disc list-inside">
                            <li>Publique um vídeo sobre a iniciativa</li>
                            <li>Dê aceite no regulamento</li>
                            <li>Todos os participantes devem confirmar a participação</li>
                          </ul>
                        </div>
                      </div>
                    ) : entrega.status === "reprovada" ? (
                      <p className="text-xs text-red-500 font-semibold mt-1">
                        Reprovada
                      </p>
                    ) : periodoConfirmacaoElbb ? (
                      <p className="text-xs text-pink-bb-1 mt-1">
                        <DateRangeDisplay
                          dataInicio={periodoConfirmacaoElbb.dataInicio}
                          dataFim={periodoConfirmacaoElbb.dataFim}
                          dateFormat="dd/MM"
                          uppercase={false}
                        />
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={onToggle}
        className="mt-3 sm:mt-4 text-blue-600 font-semibold hover:text-blue-700 active:text-blue-800 transition-colors flex items-center gap-2 text-xs sm:text-sm touch-manipulation w-full sm:w-auto justify-center sm:justify-start py-2 sm:py-0"
      >
        <FiChevronDown
          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
        {isExpanded ? "VER MENOS INFORMAÇÕES" : "VER MAIS INFORMAÇÕES"}
      </button>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 sm:p-6 max-w-md w-full shadow-xl">
            {deleteSuccess ? (
              <>
                <h3 className="text-base sm:text-lg font-bold text-green-600 mb-3 sm:mb-4">
                  Sucesso!
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Iniciativa inativada com sucesso!
                </p>
              </>
            ) : (
              <>
                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Tem certeza que deseja inativar esta iniciativa? Esta ação não poderá ser desfeita.
                </p>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm sm:text-base text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors font-semibold disabled:opacity-50 touch-manipulation"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm sm:text-base text-white bg-red-600 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-semibold disabled:opacity-50 touch-manipulation"
                  >
                    {isDeleting ? "Deletando..." : "Confirmar Exclusão"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
